import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { projects } from "../../data/projects";
import { optimizeImage } from "../../lib/optimizeImage";

const FILTERS = [
    { label: "All", tone: "bg-[#2D2A26] text-cream" },
    { label: "Brand", tone: "bg-coral/25" },
    { label: "Campaign", tone: "bg-butter/40" },
    { label: "Digital", tone: "bg-sage/45" },
    { label: "Presentation", tone: "bg-[#D9CCF2]/60" },
];

const visibleProjects = projects.filter((p) => !p.hidden);

const FilterChips = ({ active, onChange }) => (
    <div className="mb-10 md:mb-14 flex flex-wrap gap-3" data-testid="work-filters">
        {FILTERS.map((f) => {
            const on = active === f.label;
            const count = f.label === "All" ? visibleProjects.length : visibleProjects.filter((p) => p.categories?.includes(f.label)).length;
            return (
                <motion.button
                    key={f.label}
                    type="button"
                    onClick={() => onChange(f.label)}
                    whileHover={{ y: -3, rotate: on ? 0 : -2 }}
                    whileTap={{ scale: 0.95 }}
                    data-testid={`work-filter-${f.label.toLowerCase()}`}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-body font-semibold text-sm md:text-base transition-[background-color,color,box-shadow] duration-300 ${
                        on
                            ? "bg-[#2D2A26] text-cream shadow-[0_10px_24px_-14px_rgba(45,42,38,0.7)]"
                            : `${f.label === "All" ? "bg-surface-1" : f.tone} text-text-primary border border-[rgba(45,42,38,0.08)]`
                    }`}
                >
                    {f.label}
                    <span className={`text-xs ${on ? "text-cream/70" : "text-text-secondary"}`}>{count}</span>
                </motion.button>
            );
        })}
    </div>
);

const reveal = "group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100";

const ProjectTile = ({ p, i }) => (
    <motion.li
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.06 }}
    >
        <Link
            to={`/work/${p.slug}`}
            data-testid={`project-card-${p.slug}`}
            aria-label={`${p.title}, ${p.subtitle}. View case study`}
            className="group block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-4 focus-visible:ring-offset-cream"
        >
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-surface-1">
                <img
                    src={optimizeImage(p.cover, 900)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] group-focus-visible:scale-[1.03]"
                />
                <div
                    aria-hidden
                    data-testid={`project-preview-${p.slug}`}
                    className="absolute inset-0 hidden md:flex flex-col justify-end p-6 bg-[#1F1B18]/0 transition-colors duration-500 group-hover:bg-[#1F1B18]/55 group-focus-visible:bg-[#1F1B18]/55"
                >
                    <div className={`translate-y-3 opacity-0 transition-[transform,opacity] duration-500 ease-out ${reveal}`}>
                        <p className="font-heading font-semibold text-xl lg:text-2xl leading-tight text-white">{p.title}</p>
                        <p className="mt-1 font-body text-sm text-white/80">{p.subtitle}</p>
                    </div>
                </div>
            </div>
            <div className="md:hidden pt-3 pb-2" aria-hidden>
                <p className="font-heading font-semibold text-lg leading-tight text-text-primary">{p.title}</p>
                <p className="mt-0.5 font-body text-sm text-text-secondary">{p.subtitle}</p>
            </div>
        </Link>
    </motion.li>
);

export const Work = () => {
    const [active, setActive] = useState("All");
    const list = active === "All" ? visibleProjects : visibleProjects.filter((p) => p.categories?.includes(active));
    return (
        <section id="work" data-testid="work-section" className="relative pt-12 md:pt-16 pb-10 md:pb-14 scroll-mt-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="mb-12 md:mb-16">
                    <motion.span
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                        className="font-accent text-coral text-3xl md:text-4xl block rotate-[-2deg]"
                        data-testid="work-eyebrow"
                    >
                        Selected work
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                        className="font-heading text-4xl md:text-6xl font-semibold leading-[1.05] mt-2"
                        data-testid="work-title"
                    >
                        Projects that turn
                        <br />
                        complexity into clarity.
                    </motion.h2>
                </div>

                <FilterChips active={active} onChange={setActive} />

                <ul key={active} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="work-grid">
                    {list.map((p, i) => (
                        <ProjectTile key={p.slug} p={p} i={i} />
                    ))}
                </ul>

                <div className="mt-10 text-center">
                    <a
                        href="https://www.monasaedi.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="work-view-all"
                        className="pill-btn inline-flex items-center gap-2 rounded-full border border-[rgba(45,42,38,0.18)] bg-cream px-6 py-3 font-body font-semibold"
                    >
                        View the full archive
                        <span aria-hidden>→</span>
                    </a>
                </div>
            </div>
        </section>
    );
};

export default Work;
